/**
 * Bulk registration — one payment for the whole batch.
 *
 * POST /batches/{id}/create-payment-intent prices every registration that
 * is awaiting payment (fee + platform fee + processing fee) into a single
 * PaymentIntent; Stripe's Payment Element collects the card once; then
 * /batches/{id}/confirm verifies the charge server-side and confirms them
 * all. Approval-required events aren't in this charge — they're paid
 * individually once the organizer approves.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import type { EmbedClient, BatchPaymentIntentResponse } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { PaymentForm } from '@/components/PaymentForm';
import { themedStripeAppearance } from '@/styles/stripeAppearance';
import { money } from './bulkFormat';
import './PaymentPage.css';
import './Bulk.css';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '';

interface Props {
  client: EmbedClient;
}

export function BulkPaymentPage({ client }: Props) {
  const navigate = useNavigate();
  const { selectedTeam, bulkBatch, setBulkConfirmed } = useFlow();
  const [intent, setIntent] = useState<BatchPaymentIntentResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stripe = useMemo<Promise<Stripe | null> | null>(() => {
    if (!STRIPE_PUBLISHABLE_KEY) return null;
    return loadStripe(STRIPE_PUBLISHABLE_KEY);
  }, []);

  const batchId = bulkBatch?.batch_id ?? null;

  useEffect(() => {
    if (!selectedTeam || !batchId) {
      navigate('/bulk/review', { replace: true });
      return;
    }
    client
      .createBatchPaymentIntent(batchId)
      .then(setIntent)
      .catch(() => setError('Could not initialize payment. Please try again.'));
  }, [client, selectedTeam, batchId, navigate]);

  if (!selectedTeam || !batchId) return null;
  if (error) return <div role="alert" className="tn-payment__error">{error}</div>;
  if (!intent) return <div className="tn-payment__loading">Preparing checkout…</div>;

  const count = intent.registrations.length;
  const waiting = (bulkBatch?.registrations ?? []).filter((r) => r.status === 'pending_approval');

  return (
    <div className="tn-payment">
      <h1 className="tn-payment__title">Payment</h1>
      <p className="tn-payment__subtitle">
        Registering {selectedTeam.name} for {count} event{count === 1 ? '' : 's'}
      </p>

      <div className="tn-payment__breakdown">
        <div className="tn-payment__line">
          <span>Registration fees</span>
          <span>{money(intent.fee_cents)}</span>
        </div>
        {(intent.platform_fee_cents ?? 0) > 0 ? (
          <div className="tn-payment__line">
            <span>Platform fee</span>
            <span>{money(intent.platform_fee_cents ?? 0)}</span>
          </div>
        ) : null}
        {intent.processing_fee_cents > 0 ? (
          <div className="tn-payment__line">
            <span>Processing fee</span>
            <span>{money(intent.processing_fee_cents)}</span>
          </div>
        ) : null}
        <div className="tn-payment__line tn-payment__line--total">
          <span>Total</span>
          <span>{money(intent.amount_cents)}</span>
        </div>
      </div>

      {waiting.length > 0 ? (
        <p className="tn-bulk__fine">
          {waiting.map((r) => r.event_name).join(', ')} need{waiting.length === 1 ? 's' : ''} organizer
          approval first and {waiting.length === 1 ? 'isn’t' : 'aren’t'} included in this payment.
        </p>
      ) : null}

      {stripe ? (
        <Elements
          stripe={stripe}
          options={{ clientSecret: intent.client_secret, appearance: themedStripeAppearance() }}
        >
          <PaymentForm
            submitLabel={`Pay and complete ${count} registration${count === 1 ? '' : 's'}`}
            onSuccess={async () => {
              const result = await client.confirmBatch(batchId);
              setBulkConfirmed(result.registrations);
              navigate('/bulk/done');
            }}
          />
        </Elements>
      ) : (
        <div className="tn-card tn-payment__dev-skip">
          <h2>Payment (dev mode)</h2>
          <p>Stripe publishable key not set, so this batch can&rsquo;t be charged here.</p>
        </div>
      )}
    </div>
  );
}
